import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { http, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowsClockwise, Upload, DownloadSimple, Eye, EyeSlash, Broadcast, ChartLine, CaretRight, FloppyDisk, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import KickDialog from "@/components/KickDialog";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const TABS = [
  { key: "general",     label: "General" },
  { key: "services",    label: "Services" },
  { key: "settings",    label: "Settings" },
  { key: "documents",   label: "Documents" },
  { key: "statistics",  label: "Statistics" },
  { key: "inventories", label: "Inventories" },
];

const KYC_TYPES = [
  { key: "id_proof",      label: "ID Proof",         required: true, subtypes: ["Aadhaar Card", "Voter ID", "Passport", "Driving License", "PAN Card"] },
  { key: "address_proof", label: "Address Proof",    required: true, subtypes: ["Aadhar Card", "Voter ID", "Passport", "Driving License", "Electricity Bill", "Rent Agreement"] },
  { key: "photo",         label: "PassportSize Photo", required: true, subtypes: [] },
  { key: "signature",     label: "Signature",        required: false, subtypes: [] },
  { key: "caf",           label: "CAF Form",         required: false, subtypes: [] },
];

const fileToBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result).split(",")[1] || "");
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function UserPortal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Persist active tab in the URL (?tab=statistics) so an F5 refresh keeps you
  // on the same tab instead of snapping back to General. Also lets deep-links
  // like /users/<id>?tab=statistics work.
  const urlTab = searchParams.get("tab");
  const [tab, setTabState] = useState(urlTab && TABS.some(t => t.key === urlTab) ? urlTab : "general");
  // Keep tab state in sync with the URL — otherwise Services → "Add Credit"
  // navigates to `?tab=general&recharge=1` but the UI stays on Services because
  // `useState` only reads the URL on mount.
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && TABS.some(x => x.key === t) && t !== tab) setTabState(t);
  }, [searchParams, tab]);
  const setTab = (t) => {
    setTabState(t);
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", t);
    setSearchParams(sp, { replace: true });
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [nasList, setNasList] = useState([]);
  const [olts, setOlts] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [prof, pl, ns, ol] = await Promise.all([
        http.get(`/subscribers/${id}/profile`),
        http.get("/plans"),
        http.get("/nas"),
        http.get("/olts"),
      ]);
      setData(prof.data); setPlans(pl.data); setNasList(ns.data); setOlts(ol.data);
    } catch (err) {
      toast.error("Failed to load user");
      navigate("/subscribers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  if (loading || !data) return <div className="p-10 text-muted-foreground font-mono text-sm">Loading user…</div>;
  const s = data.subscriber;

  const patch = async (changes) => {
    setSaving(true);
    try {
      await http.patch(`/subscribers/${s.id}`, changes);
      toast.success("Saved");
      await load();          // hard-refresh from server so General tab shows edited values
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 fade-up" data-testid="user-portal-view">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3 border-b border-border pb-4">
        <div>
          <h1 className="font-display font-bold text-3xl">Edit user — <span className="text-primary">{s.username}</span></h1>
          <div className="text-xs text-muted-foreground mt-1">{s.full_name}</div>
        </div>
        <div className="text-xs font-mono text-muted-foreground inline-flex items-center gap-2">
          <Link to="/subscribers" className="hover:text-primary" data-testid="portal-crumb-users">User</Link>
          <CaretRight size={10} />
          <span>Edit user — {s.username}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border flex gap-6 overflow-x-auto" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`text-sm py-2.5 border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "general"     && <GeneralTab data={{ ...data, nas_list: nasList }} onReload={load} />}
      {tab === "services"    && <ServicesTab data={data} plans={plans} nasList={nasList} onSave={patch} saving={saving} />}
      {tab === "settings"    && <SettingsTab data={data} onSave={patch} saving={saving} />}
      {tab === "documents"   && <DocumentsTab sub={s} reload={load} />}
      {tab === "statistics"  && <StatisticsTab sub={s} />}
      {tab === "inventories" && <InventoriesTab data={data} olts={olts} onSave={patch} saving={saving} />}
    </div>
  );
}

/* ---------------- GENERAL ---------------- */
function GeneralTab({ data, onReload }) {
  const s = data.subscriber;
  const p = data.plan || {};
  const b = data.billing || {};
  const sess = data.session || {};
  const nas = data.nas || {};
  const [rechargeOpen, setRechargeOpen] = useState(false);
  // Auto-open recharge when redirected from Services side panel with ?recharge=1.
  // `useLocation()` is reactive, so every nav to `/users/x?tab=general&recharge=1`
  // — including re-clicks on the same URL — triggers this effect. We also strip
  // the param after opening so tapping "Add Credit" a second time still re-fires.
  const location = useLocation();
  const [, setSp2] = useSearchParams();
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("recharge") === "1") {
      setRechargeOpen(true);
      sp.delete("recharge");
      setSp2(sp, { replace: true });
    }
  }, [location.search, location.key]);
  const [topup, setTopup] = useState({ amount: "", method: "cash", reference: "", note: "" });
  const [posting, setPosting] = useState(false);
  const submitRecharge = async (e) => {
    e.preventDefault();
    const amt = Number(topup.amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    setPosting(true);
    try {
      const { data: r } = await http.post(`/subscribers/${s.id}/wallet/topup`, { ...topup, amount: amt });
      toast.success(`Recharged ₹${amt} · Balance ₹${r.balance}${r.auto_settled?.length ? ` · Settled ${r.auto_settled.join(", ")}` : ""}`);
      setRechargeOpen(false); setTopup({ amount: "", method: "cash", reference: "", note: "" });
      onReload && onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Recharge failed"); }
    finally { setPosting(false); }
  };
  const speed = p.speed_mbps ? `${p.speed_mbps * 1000} Kbps / ${p.speed_mbps * 1000} Kbps` : "—";

  // Full editable form (bottom half) — matches SNS reference screenshot
  const [f, setF] = useState({});
  const [changePw, setChangePw] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPortalPw, setShowPortalPw] = useState(false);
  const [changePortalPw, setChangePortalPw] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setF({
      full_name: s.full_name || "", company_name: s.company_name || "",
      address: s.address || "", city: s.city || "", pincode: s.pincode || "", state: s.state || "",
      country: s.country || "", phone_no: s.phone_no || "", phone: s.phone || "",
      email: s.email || "", gstin: s.gstin || "",
      username: s.username || "", password: "",
      mac_address: s.mac_address || "", portal_login: s.username || "",
      portal_password: "",
      nas_id: s.nas_id || "", user_group: s.user_group || "Default",
      comment: s.comment || "",
      status: s.status || "active",
      latitude: s.latitude ?? "",
      longitude: s.longitude ?? "",
    });
    setChangePw(false); setChangePortalPw(false);
  }, [s.id, s.full_name, s.address, s.city, s.state, s.country, s.pincode, s.phone, s.phone_no, s.email, s.gstin, s.username, s.mac_address, s.nas_id, s.company_name, s.user_group, s.comment, s.status, s.latitude, s.longitude]);
  const nasList = data.nas_list || (nas.id ? [nas] : []);
  const submitForm = async (e) => {
    e.preventDefault();
    if (!f.full_name || !f.phone) return toast.error("Full name and Mobile no are required");
    const payload = {
      full_name: f.full_name, company_name: f.company_name, address: f.address,
      city: f.city, pincode: f.pincode, state: f.state, country: f.country,
      phone_no: f.phone_no, phone: f.phone, email: f.email, gstin: f.gstin,
      username: f.username, mac_address: f.mac_address, nas_id: f.nas_id,
      user_group: f.user_group, comment: f.comment, status: f.status,
    };
    // Coordinates are optional — only send when the operator provided both.
    const latNum = f.latitude === "" || f.latitude === null ? null : Number(f.latitude);
    const lngNum = f.longitude === "" || f.longitude === null ? null : Number(f.longitude);
    if (latNum !== null && !Number.isNaN(latNum)) payload.latitude = latNum;
    if (lngNum !== null && !Number.isNaN(lngNum)) payload.longitude = lngNum;
    if (changePw && f.password) payload.password = f.password;
    if (changePortalPw && f.portal_password) payload.portal_password = f.portal_password;
    setSaving(true);
    try {
      await http.patch(`/subscribers/${s.id}`, payload);
      toast.success("Saved");
      await onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const badge = (val, tone = "success") => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-mono ${tone === "success" ? "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))]" : "bg-[hsl(var(--muted))] text-muted-foreground"}`}>{val}</span>
  );
  const numeric_id = ((s.created_at || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 90000) + 1000;

  return (
    <div data-testid="general-tab">
      {/* Read-only summary grid (SNS-style) */}
      <div className="border border-primary/40 bg-card mb-6">
        <table className="w-full text-sm">
          <tbody>
            <RowPair k1="Account Type" v1="Regular" k2="Account Status" v2={badge(s.status || "active")} k3="Conn. status" v3={badge(`${s.connection_status || "offline"}${sess.live ? "(1)" : ""}`, s.connection_status === "online" ? "success" : "muted")} k4="Last Logoff" v4={sess.last_logoff ? fmtDate(sess.last_logoff) : "—"} />
            <RowPair k1="Rate (UP/DOWN)" v1={speed} k2="DataLimit" v2={p.data_limit_gb ? `${p.data_limit_gb} GB` : "Unlimited"} k3="UptimeLimit" v3="Unlimited" k4="ExpiryDate" v4={s.expires_at || "—"} />
            <RowPair k1="Last CreditDate" v1={b.last_credit_date ? fmtDate(b.last_credit_date) : "—"} k2="Last CreditBy" v2={b.last_credit_by || "—"} k3="Registered on" v3={s.created_at ? fmtDate(s.created_at) : "—"} k4="Registered By" v4={data.org?.registered_by || "—"} />
            <RowPair k1="Billing Type" v1="Prepaid" k2="Wallet Balance" v2={<div className="flex items-center gap-2"><span>₹ {s.wallet_balance ?? b.wallet_balance ?? 0}</span><button onClick={() => setRechargeOpen(true)} data-testid="recharge-btn" className="text-[10px] uppercase font-mono px-2 py-0.5 border border-primary/40 text-primary hover:bg-primary/10 rounded-sm">+ Recharge</button></div>} k3="Total Paid Amount" v3={`₹ ${b.total_paid ?? 0}`} k4="Total Unpaid Amount" v4={`₹ ${b.total_unpaid ?? 0}`} />
            <RowPair k1="IPv4 Address" v1={s.ip_address || "N/A"} k2="IPv6 Prefix" v2="N/A" k3="IPv6 Delegation" v3="N/A" k4="Allowed NAS" v4={nas.name ? <div>1) {nas.name}</div> : "—"} />
            <RowPair
              k1="Router MAC (CPE)"
              v1={<span className="font-mono">{sess.live?.mac || sess.last?.mac || s.mac_address || "N/A"}</span>}
              k2="ONU MAC"
              v2={<span className="font-mono text-muted-foreground">{s.onu_mac || "N/A"}</span>}
              k3="Inventories Sr No"
              v3={s.onu_serial || "N/A"}
              k4="ONU Name (description)"
              v4={<span className="text-muted-foreground truncate max-w-[160px] inline-block align-bottom" title={s.onu_description}>{s.onu_description || "—"}</span>}
            />
            <RowPair
              k1="Signal (RX dBm)"
              v1={(() => {
                const rx = s.signal_dbm;
                if (typeof rx !== "number") return <span className="text-muted-foreground italic">not polled yet</span>;
                const low = p.rx_low_dbm, high = p.rx_high_dbm;
                let tone = "text-[hsl(var(--success))]";
                let hint = "within plan window";
                if (low != null && rx < low)       { tone = "text-[hsl(var(--destructive))]"; hint = `below plan low ${low} dBm`; }
                else if (high != null && rx > high) { tone = "text-[hsl(var(--warning))]"; hint = `above plan high ${high} dBm`; }
                else if (low == null && high == null) { tone = "text-foreground"; hint = "no plan threshold set"; }
                return <span className={`font-mono ${tone}`} title={hint} data-testid="general-rx">{rx.toFixed(1)} dBm</span>;
              })()}
              k2="OLT Name"
              v2={<span className="font-mono">{data.olt?.name || "—"}</span>}
              k3="OLT Port"
              v3={<span className="font-mono">{s.olt_port ?? "—"}</span>}
              k4="Last Seen"
              v4={s.last_seen_at ? fmtDate(s.last_seen_at) : "—"}
            />
          </tbody>
        </table>
      </div>

      {/* Editable form — two columns */}
      <form onSubmit={submitForm} className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4" data-testid="general-edit-form">
        {/* LEFT */}
        <F label="Full Name*"><Input required data-testid="gf-full-name" value={f.full_name || ""} onChange={(e) => setF({ ...f, full_name: e.target.value })} /></F>
        <RO label="ID*" value={numeric_id} testId="gf-id" />
        <F label="Company Name"><Input data-testid="gf-company" value={f.company_name || ""} onChange={(e) => setF({ ...f, company_name: e.target.value })} placeholder="Enter company name" /></F>
        <RO label="Username*" value={f.username || "—"} testId="gf-username" />
        <F label="Address"><textarea rows={2} data-testid="gf-address" value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} className="w-full bg-secondary/40 border border-border rounded-sm px-3 py-2 text-sm" /></F>
        <div>
          <div className="flex items-baseline gap-3">
            <Label className="uppercase-label">Password</Label>
            <label className="text-[10px] text-muted-foreground inline-flex items-center gap-1"><input type="checkbox" checked={changePw} onChange={(e) => setChangePw(e.target.checked)} data-testid="gf-change-pw" /> Do you want to change the password?</label>
          </div>
          <div className="relative mt-1.5">
            <Input
              type={showPw ? "text" : "password"}
              readOnly={!changePw}
              placeholder={changePw ? "New PPPoE password" : "•••••••"}
              value={changePw ? (f.password || "") : (showPw ? (s.password || "") : (s.password ? "••••••••" : ""))}
              onChange={(e) => setF({ ...f, password: e.target.value })}
              className={`font-mono pr-9 ${!changePw ? "opacity-70 cursor-default" : ""}`}
              data-testid="gf-password"
            />
            <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="gf-password-show"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    title={showPw ? "Hide password" : "Show password"}>
              {showPw ? <EyeSlash size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <F label="City"><Input data-testid="gf-city" value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="Enter city" /></F>
        <F label="MAC Address">
          <div className="flex gap-2">
            <Input data-testid="gf-mac" value={f.mac_address || ""} onChange={(e) => setF({ ...f, mac_address: e.target.value.toUpperCase() })} className="font-mono" placeholder="AA:BB:CC:DD:EE:FF" />
            <Button type="button" variant="outline" size="sm" data-testid="gf-mac-reset"
                    className="rounded-sm text-xs h-9 whitespace-nowrap"
                    onClick={async () => {
                      if (!window.confirm("Reset this subscriber's MAC address? The next dial-in from any device will be accepted and re-locked.")) return;
                      try {
                        await http.post(`/subscribers/${s.id}/reset-mac`);
                        toast.success("MAC address cleared — dial in from the new CPE now");
                        setF((prev) => ({ ...prev, mac_address: "" }));
                        onReload && onReload();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
                    }}>
              <ArrowsClockwise size={12} className="mr-1" /> Reset
            </Button>
          </div>
        </F>
        <F label="Zip"><Input data-testid="gf-zip" value={f.pincode || ""} onChange={(e) => setF({ ...f, pincode: e.target.value })} placeholder="Enter zip" /></F>
        <RO label="Portal login" value={f.portal_login || "—"} testId="gf-portal-login" />
        <F label="State"><Input data-testid="gf-state" value={f.state || ""} onChange={(e) => setF({ ...f, state: e.target.value })} placeholder="Enter state" /></F>
        <div>
          <div className="flex items-baseline gap-3">
            <Label className="uppercase-label">Portal password</Label>
            <label className="text-[10px] text-muted-foreground inline-flex items-center gap-1"><input type="checkbox" checked={changePortalPw} onChange={(e) => setChangePortalPw(e.target.checked)} data-testid="gf-change-portalpw" /> Do you want to change the portal password?</label>
          </div>
          <div className="relative mt-1.5">
            <Input
              type={showPortalPw ? "text" : "password"}
              readOnly={!changePortalPw}
              placeholder={changePortalPw ? "New portal password" : "•••••••"}
              value={changePortalPw
                ? (f.portal_password || "")
                : (showPortalPw
                    ? (s.portal_password_plain || s.portal_password || "(hashed — not recoverable)")
                    : (s.portal_password_plain || s.portal_password ? "••••••••" : ""))}
              onChange={(e) => setF({ ...f, portal_password: e.target.value })}
              className={`font-mono pr-9 ${!changePortalPw ? "opacity-70 cursor-default" : ""}`}
              data-testid="gf-portal-password"
            />
            <button type="button" onClick={() => setShowPortalPw((v) => !v)} data-testid="gf-portal-password-show"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    title={showPortalPw ? "Hide password" : "Show password"}>
              {showPortalPw ? <EyeSlash size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <F label="Country"><Input data-testid="gf-country" value={f.country || ""} onChange={(e) => setF({ ...f, country: e.target.value })} placeholder="Enter country" /></F>
        <F label="Operator*"><Select key={`op-${nasList.length}-${f.nas_id || "none"}`} value={f.nas_id || undefined} onValueChange={(v) => setF({ ...f, nas_id: v })}><SelectTrigger data-testid="gf-operator"><SelectValue placeholder="Select NAS" /></SelectTrigger><SelectContent>{nasList.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}</SelectContent></Select></F>
        <F label="Phone no"><Input data-testid="gf-phone-no" value={f.phone_no || ""} onChange={(e) => setF({ ...f, phone_no: e.target.value })} placeholder="Enter phone no" /></F>
        <F label="User Group*"><Select value={f.user_group || "Default"} onValueChange={(v) => setF({ ...f, user_group: v })}><SelectTrigger data-testid="gf-usergroup"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Default">Default</SelectItem><SelectItem value="Premium">Premium</SelectItem><SelectItem value="Business">Business</SelectItem></SelectContent></Select></F>
        <F label="Mobile no*"><Input required data-testid="gf-mobile" value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} className="font-mono" /></F>
        <F label="Comment"><textarea rows={2} data-testid="gf-comment" value={f.comment || ""} onChange={(e) => setF({ ...f, comment: e.target.value })} className="w-full bg-secondary/40 border border-border rounded-sm px-3 py-2 text-sm" placeholder="Enter comment" /></F>
        <F label="Email"><Input type="email" data-testid="gf-email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} /></F>
        <div>
          <Label className="uppercase-label">Status*</Label>
          <div className="mt-2 flex items-center gap-4 text-sm">
            {["active", "suspended"].map((v) => (
              <label key={v} className="inline-flex items-center gap-1.5">
                <input type="radio" name="gf-status" checked={f.status === v} onChange={() => setF({ ...f, status: v })} data-testid={`gf-status-${v}`} />
                <span className="capitalize">{v === "suspended" ? "Inactive" : "Active"}</span>
              </label>
            ))}
          </div>
        </div>
        <F label="GST no"><Input data-testid="gf-gst" value={f.gstin || ""} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })} className="font-mono" placeholder="Enter GST no" /></F>
        <div />

        {/* Geo-coordinates — appear on Client Map + Dashboard map once saved. */}
        <F label="Latitude">
          <div className="flex gap-2">
            <Input type="number" step="0.000001" data-testid="gf-lat"
                   value={f.latitude ?? ""} onChange={(e) => setF({ ...f, latitude: e.target.value })}
                   className="font-mono" placeholder="e.g. 21.6086" />
            <Button type="button" variant="outline" size="sm" data-testid="gf-locate-me"
                    className="rounded-sm text-xs h-9 whitespace-nowrap"
                    onClick={() => {
                      if (!navigator.geolocation) return toast.error("Geolocation not supported on this device");
                      const t = toast.loading("Getting current location…");
                      navigator.geolocation.getCurrentPosition(
                        (pos) => { toast.dismiss(t); setF((prev) => ({ ...prev, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) })); toast.success("Location captured — click Submit to save"); },
                        (err) => { toast.dismiss(t); toast.error(err.message || "Location denied"); },
                        { enableHighAccuracy: true, timeout: 10000 }
                      );
                    }}>
              <MapPin size={12} className="mr-1" /> Use my location
            </Button>
          </div>
        </F>
        <F label="Longitude">
          <div className="flex gap-2">
            <Input type="number" step="0.000001" data-testid="gf-lng"
                   value={f.longitude ?? ""} onChange={(e) => setF({ ...f, longitude: e.target.value })}
                   className="font-mono" placeholder="e.g. 71.2200" />
            {(f.latitude && f.longitude) && (
              <Button type="button" variant="outline" size="sm" data-testid="gf-open-map"
                      className="rounded-sm text-xs h-9 whitespace-nowrap"
                      onClick={() => window.open(`https://www.google.com/maps?q=${f.latitude},${f.longitude}`, "_blank")}>
                Open in Maps
              </Button>
            )}
          </div>
        </F>

        <div className="lg:col-span-2 flex items-center justify-center gap-3 pt-4">
          <Button type="submit" data-testid="gf-submit" disabled={saving} className="rounded-sm bg-primary hover:bg-primary/90 h-10 px-8"><FloppyDisk size={14} className="mr-2" />{saving ? "Saving…" : "Submit"}</Button>
          <Button type="button" variant="outline" data-testid="gf-back" onClick={() => window.history.back()} className="rounded-sm h-10 px-8 border-[hsl(var(--destructive)/0.5)] text-[hsl(var(--destructive))]">Back</Button>
        </div>
      </form>

      {/* Recharge dialog */}
      <Dialog open={rechargeOpen} onOpenChange={setRechargeOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="recharge-modal">
          <DialogHeader><DialogTitle className="font-display">Recharge wallet — {s.username}</DialogTitle></DialogHeader>
          <form onSubmit={submitRecharge} className="space-y-3">
            <div>
              <Label className="uppercase-label">Amount (₹)</Label>
              <Input type="number" min="1" step="0.01" required data-testid="rc-amount" value={topup.amount} onChange={(e) => setTopup({ ...topup, amount: e.target.value })} className="mt-1.5 font-mono" placeholder="e.g. 500" />
            </div>
            <div>
              <Label className="uppercase-label">Method</Label>
              <Select value={topup.method} onValueChange={(v) => setTopup({ ...topup, method: v })}>
                <SelectTrigger data-testid="rc-method" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="adjustment">Adjustment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="uppercase-label">Reference (optional)</Label>
              <Input data-testid="rc-ref" value={topup.reference} onChange={(e) => setTopup({ ...topup, reference: e.target.value })} className="mt-1.5 font-mono" placeholder="UPI txn ID / receipt #" />
            </div>
            <div>
              <Label className="uppercase-label">Note</Label>
              <Input data-testid="rc-note" value={topup.note} onChange={(e) => setTopup({ ...topup, note: e.target.value })} className="mt-1.5" placeholder="e.g. Aug month topup" />
            </div>
            <div className="text-[10px] text-muted-foreground italic">Any pending invoices will be auto-settled from wallet after topup.</div>
            <Button type="submit" data-testid="rc-submit" disabled={posting} className="w-full rounded-sm bg-primary hover:bg-primary/90">{posting ? "Processing…" : "Recharge"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function RowPair({ k1, v1, k2, v2, k3, v3, k4, v4 }) {
  return (
    <tr className="border-b border-border last:border-b-0">
      <Cell k={k1} v={v1} />
      <Cell k={k2} v={v2} />
      <Cell k={k3} v={v3} />
      <Cell k={k4} v={v4} last />
    </tr>
  );
}
function Cell({ k, v, last }) {
  if (!k) return <td className={`p-3 ${!last ? "border-r border-border" : ""}`} />;
  // Read-only summary cells are intentionally muted (opacity-70) so the
  // editable form below stands out as the primary interaction surface.
  return (
    <td className={`p-3 align-top ${!last ? "border-r border-border" : ""}`}>
      <div className="text-[11px] text-muted-foreground/70 font-semibold uppercase tracking-wide">{k}</div>
      <div className="text-sm mt-1 text-foreground/70">{v}</div>
    </td>
  );
}

/* ---------------- SERVICES ---------------- */
const SVC_SIDE_ACTIONS = [
  { key: "add-credit",   label: "Add credit",         icon: "Plus" },
  { key: "add-deposit",  label: "Add Deposit",        icon: "Plus" },
  { key: "override-bw",  label: "Override Bandwidth", icon: "ArrowsClockwise" },
  { key: "list-invoice", label: "List Invoice",       icon: "ChartLine" },
  { key: "svc-history",  label: "Service History",    icon: "ArrowsClockwise" },
  { key: "add-schedule", label: "Add Schedule",       icon: "Plus" },
  { key: "list-schedule",label: "List Schedule",      icon: "ChartLine" },
  { key: "wallet",       label: "Wallet",             icon: "CaretRight" },
  { key: "statements",   label: "Statement(s)",       icon: "CaretRight" },
];

function ServicesTab({ data, plans, nasList, onSave, saving }) {
  const s = data.subscriber;
  const nav = useNavigate();
  const [ipPools, setIpPools] = useState([]);       // all v4 pools
  const [availableIps, setAvailableIps] = useState([]);
  const [form, setForm] = useState({
    plan_id: s.plan_id || "",
    nas_id: s.nas_id || "",
    concurrent_user: s.concurrent_user ?? 1,
    cpe_mac_addresses: s.cpe_mac_addresses || (s.mac_address || ""),
    allow_mac_only: s.allow_mac_only ?? true,
    ipv4_mode: s.ipv4_mode || (s.ip_address ? "static" : "none"),
    static_pool_id: s.static_pool_id || "",
    ip_address: s.ip_address || "",
    static_ip_expires_at: s.static_ip_expires_at || "",
    ipv6_prefix_enable: !!s.ipv6_prefix_enable,
    ipv6_delegation_enable: !!s.ipv6_delegation_enable,
    enable_billing: s.enable_billing ?? true,
    expires_at: s.expires_at || "",
  });
  useEffect(() => {
    setForm({
      plan_id: s.plan_id || "", nas_id: s.nas_id || "",
      concurrent_user: s.concurrent_user ?? 1,
      cpe_mac_addresses: s.cpe_mac_addresses || (s.mac_address || ""),
      allow_mac_only: s.allow_mac_only ?? true,
      ipv4_mode: s.ipv4_mode || (s.ip_address ? "static" : "none"),
      static_pool_id: s.static_pool_id || "",
      ip_address: s.ip_address || "",
      static_ip_expires_at: s.static_ip_expires_at || "",
      ipv6_prefix_enable: !!s.ipv6_prefix_enable,
      ipv6_delegation_enable: !!s.ipv6_delegation_enable,
      enable_billing: s.enable_billing ?? true,
      expires_at: s.expires_at || "",
    });
  }, [s.id]); // eslint-disable-line
  // Load all v4 pools on mount so the Static Pool dropdown lists newly created
  // pools without needing to change the plan. Filter by NAS mapping when set.
  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await http.get("/ip-pools");
        setIpPools((r || []).filter((p) => (p.family || "v4") === "v4"));
      } catch { setIpPools([]); }
    })();
  }, [s.id]);
  const filteredPools = ipPools.filter((p) => !p.nas_ids || p.nas_ids.length === 0 || (form.nas_id && p.nas_ids.includes(form.nas_id)));
  // Fetch free IPs whenever the selected static pool changes
  useEffect(() => {
    if (form.ipv4_mode !== "static" || !form.static_pool_id) { setAvailableIps([]); return; }
    (async () => {
      try {
        const { data: r } = await http.get(`/ip-pools/${form.static_pool_id}/available-ips?limit=500&exclude_sub_id=${s.id}`);
        setAvailableIps(r.available || []);
      } catch { setAvailableIps([]); }
    })();
  }, [form.ipv4_mode, form.static_pool_id, s.id]);
  const go = (key) => {
    if (key === "list-invoice" || key === "statements") nav(`/invoices?subscriber_id=${s.id}`);
    else if (key === "svc-history") nav(`/users/${s.id}?tab=statistics`);
    else if (key === "wallet" || key === "add-credit" || key === "add-deposit") {
      // Append a tick so each click yields a fresh location.key even when the
      // URL is otherwise identical — otherwise React-Router dedupes the nav
      // and the recharge effect never re-fires.
      nav(`/users/${s.id}?tab=general&recharge=1&_=${Date.now()}`);
    }
    else toast.info(`${key.replace(/-/g, " ")} — coming soon`);
  };
  const submit = (e) => {
    e.preventDefault();
    if (!form.enable_billing && !form.expires_at) {
      toast.error("Expiry Date is required when billing is disabled.");
      return;
    }
    if (form.ipv4_mode === "static" && !form.ip_address) {
      toast.error("Pick a Static Pool and IPv4 Address, or switch mode to None / DHCP Pool.");
      return;
    }
    const body = { ...form };
    if (body.ipv4_mode !== "static") { body.ip_address = ""; body.static_ip_expires_at = ""; body.static_pool_id = ""; }
    onSave(body);
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6" data-testid="services-tab">
      {/* Left quick actions */}
      <aside className="space-y-2">
        {SVC_SIDE_ACTIONS.map((a) => (
          <button key={a.key} type="button" onClick={() => go(a.key)} data-testid={`svc-side-${a.key}`}
                  className="w-full text-left px-3 py-2 bg-[hsl(var(--muted))] hover:bg-primary/20 border border-border rounded-sm text-xs font-mono uppercase tracking-wide">
            <span className="text-primary mr-2">+</span>{a.label}
          </button>
        ))}
      </aside>

      <form onSubmit={submit} className="space-y-8">
        {/* Policy */}
        <section>
          <SectionTitle label="Policy Configuration" required />
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-y-4 gap-x-6 items-center">
            <Label className="uppercase-label">Service/plan *</Label>
            <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
              <SelectTrigger data-testid="svc-plan"><SelectValue placeholder="Select plan" /></SelectTrigger>
              <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.speed_mbps} Mbps · ₹{p.price}</SelectItem>)}</SelectContent>
            </Select>
            <Label className="uppercase-label">NAS *</Label>
            <Select value={form.nas_id} onValueChange={(v) => setForm({ ...form, nas_id: v })}>
              <SelectTrigger data-testid="svc-nas"><SelectValue placeholder="Select NAS" /></SelectTrigger>
              <SelectContent>{nasList.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}</SelectContent>
            </Select>
            <Label className="uppercase-label">Concurrent User *</Label>
            <Input type="number" min="1" data-testid="svc-concurrent" value={form.concurrent_user}
                   onChange={(e) => setForm({ ...form, concurrent_user: Number(e.target.value || 1) })}
                   className="font-mono max-w-xs" />
            <Label className="uppercase-label pt-1 self-start">CPE MAC-Address *</Label>
            <div>
              <textarea rows={2} data-testid="svc-cpe-mac" value={form.cpe_mac_addresses}
                        onChange={(e) => setForm({ ...form, cpe_mac_addresses: e.target.value.toUpperCase() })}
                        className="w-full bg-secondary/40 border border-border rounded-sm px-3 py-2 text-sm font-mono"
                        placeholder="AA:BB:CC:DD:EE:FF (one per line)" />
              <label className="mt-2 inline-flex items-center gap-2 text-xs">
                <input type="checkbox" checked={form.allow_mac_only} data-testid="svc-allow-mac-only"
                       onChange={(e) => setForm({ ...form, allow_mac_only: e.target.checked })} />
                Allow this MAC(s) Only
              </label>
            </div>
          </div>
        </section>

        {/* IPv4 */}
        <section>
          <SectionTitle label="IPv4 Configuration" required />
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-y-4 gap-x-6 items-center">
            <Label className="uppercase-label">IPv4 Mode *</Label>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              {[{ v: "none", l: "None" }, { v: "static", l: "Static IP" }, { v: "pool", l: "DHCP IP Pool" }].map((o) => (
                <label key={o.v} className="inline-flex items-center gap-2">
                  <input type="radio" name="ipv4-mode" checked={form.ipv4_mode === o.v}
                         onChange={() => setForm({ ...form, ipv4_mode: o.v })}
                         data-testid={`svc-ipv4-${o.v}`} />
                  {o.l}
                </label>
              ))}
            </div>
            {form.ipv4_mode === "static" && (
              <>
                <Label className="uppercase-label">Static IPv4 *</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-xl">
                  <Select value={form.static_pool_id} onValueChange={(v) => setForm({ ...form, static_pool_id: v, ip_address: "" })}>
                    <SelectTrigger data-testid="svc-static-pool"><SelectValue placeholder="---Select Static Pool---" /></SelectTrigger>
                    <SelectContent>
                      {filteredPools.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No IPv4 pools defined — create one under IP Pools</div>}
                      {filteredPools.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.range_start}–{p.range_end} · free {p.free}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={form.ip_address} onValueChange={(v) => setForm({ ...form, ip_address: v })} disabled={!form.static_pool_id}>
                    <SelectTrigger data-testid="svc-static-ip"><SelectValue placeholder={!form.static_pool_id ? "---Select IPv4 Address---" : (availableIps.length ? `Pick from ${availableIps.length} free` : "No free IPs in pool")} /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {form.ip_address && !availableIps.includes(form.ip_address) && (
                        <SelectItem value={form.ip_address}>{form.ip_address} (current)</SelectItem>
                      )}
                      {availableIps.map((ip) => <SelectItem key={ip} value={ip}>{ip}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Label className="uppercase-label">IPv4 ExpiryDate</Label>
                <div className="max-w-sm">
                  <Input type="date" data-testid="svc-static-ip-expiry" className="font-mono"
                         value={(form.static_ip_expires_at || "").slice(0, 10)}
                         onChange={(e) => setForm({ ...form, static_ip_expires_at: e.target.value })}
                         placeholder="0000-00-00" />
                  <div className="text-[11px] text-muted-foreground mt-1">Blank = permanent. On expiry, IP is released back to the pool at next auth.</div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* IPv6 */}
        <section>
          <SectionTitle label="IPv6 Configuration" required />
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-y-4 gap-x-6 items-center">
            <Label className="uppercase-label">IPv6 Prefix Enable *</Label>
            <input type="checkbox" data-testid="svc-ipv6-prefix"
                   checked={form.ipv6_prefix_enable}
                   onChange={(e) => setForm({ ...form, ipv6_prefix_enable: e.target.checked })} />
            <Label className="uppercase-label">IPv6 Delegation Enable *</Label>
            <input type="checkbox" data-testid="svc-ipv6-delegation"
                   checked={form.ipv6_delegation_enable}
                   onChange={(e) => setForm({ ...form, ipv6_delegation_enable: e.target.checked })} />
          </div>
        </section>

        {/* Billing */}
        <section>
          <SectionTitle label="DATA Accounting/Billing" required />
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-y-4 gap-x-6 items-center">
            <Label className="uppercase-label">Enable Billing *</Label>
            <input type="checkbox" data-testid="svc-enable-billing"
                   checked={form.enable_billing}
                   onChange={(e) => setForm({ ...form, enable_billing: e.target.checked })} />
            {!form.enable_billing && (
              <>
                <Label className="uppercase-label text-destructive">Expiry Date *</Label>
                <div className="max-w-sm">
                  <Input type="date" required data-testid="svc-expiry-date" className="font-mono border-destructive/60"
                         value={(form.expires_at || "").slice(0, 10)}
                         onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
                  <div className="text-[11px] text-destructive mt-1">Required when billing is disabled — the RADIUS auth rejects the user past this date.</div>
                </div>
              </>
            )}
          </div>
        </section>

        <div className="flex justify-center">
          <Button type="submit" disabled={saving} data-testid="svc-save" className="rounded-sm h-10 px-8">
            <FloppyDisk size={14} className="mr-2" /> {saving ? "Saving…" : "Update"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function SectionTitle({ label, required }) {
  return (
    <div className="border-b border-primary/40 pb-1 mb-4">
      <span className="text-primary text-xs uppercase tracking-wide font-mono">{required && <span className="text-destructive">*</span>}{label}</span>
    </div>
  );
}

/* ---------------- SETTINGS ---------------- */
function SettingsTab({ data, onSave, saving }) {
  const s = data.subscriber;
  const [form, setForm] = useState({
    sms_alert: s.sms_alert ?? true,
    email_alert: s.email_alert ?? true,
    autorenew: !!s.autorenew,
    autorenew_with_wallet: s.autorenew_with_wallet ?? true,
    billing_type: s.billing_type || "prepaid",
    enable_ucp: s.enable_ucp ?? true,
    service_group: s.service_group || "Default",
    catch_password: !!s.catch_password,
  });
  useEffect(() => {
    setForm({
      sms_alert: s.sms_alert ?? true, email_alert: s.email_alert ?? true,
      autorenew: !!s.autorenew, autorenew_with_wallet: s.autorenew_with_wallet ?? true,
      billing_type: s.billing_type || "prepaid", enable_ucp: s.enable_ucp ?? true,
      service_group: s.service_group || "Default", catch_password: !!s.catch_password,
    });
  }, [s.id]); // eslint-disable-line
  const Row = ({ label, testid, children }) => (
    <div className="grid grid-cols-[220px_1fr] gap-4 items-center py-2">
      <Label className="uppercase-label" htmlFor={testid}>{label}</Label>
      {children}
    </div>
  );
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="max-w-3xl mx-auto space-y-8" data-testid="settings-tab">
      <section>
        <SectionTitle label="Alerts" required />
        <Row label="SMS Alert"><input type="checkbox" data-testid="set-sms" checked={form.sms_alert} onChange={(e) => setForm({ ...form, sms_alert: e.target.checked })} /></Row>
        <Row label="Email Alert"><input type="checkbox" data-testid="set-email" checked={form.email_alert} onChange={(e) => setForm({ ...form, email_alert: e.target.checked })} /></Row>
      </section>
      <section>
        <SectionTitle label="Billing" required />
        <Row label="AutoRenew"><input type="checkbox" data-testid="set-autorenew" checked={form.autorenew} onChange={(e) => setForm({ ...form, autorenew: e.target.checked })} /></Row>
        <Row label="Autorenew With Wallet"><input type="checkbox" data-testid="set-autorenew-wallet" checked={form.autorenew_with_wallet} onChange={(e) => setForm({ ...form, autorenew_with_wallet: e.target.checked })} /></Row>
        <Row label="Billing Type *">
          <div className="flex items-center gap-6 text-sm">
            {[{ v: "prepaid", l: "Prepaid" }, { v: "postpaid", l: "Postpaid" }].map((o) => (
              <label key={o.v} className="inline-flex items-center gap-2">
                <input type="radio" name="billing-type" checked={form.billing_type === o.v} onChange={() => setForm({ ...form, billing_type: o.v })} data-testid={`set-billing-${o.v}`} />
                {o.l}
              </label>
            ))}
          </div>
        </Row>
      </section>
      <section>
        <SectionTitle label="UCP" required />
        <Row label="Enable UCP"><input type="checkbox" data-testid="set-enable-ucp" checked={form.enable_ucp} onChange={(e) => setForm({ ...form, enable_ucp: e.target.checked })} /></Row>
        <Row label="ServiceGroup *">
          <Select value={form.service_group} onValueChange={(v) => setForm({ ...form, service_group: v })}>
            <SelectTrigger data-testid="set-service-group" className="max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Default">Default</SelectItem>
              <SelectItem value="Home">Home</SelectItem>
              <SelectItem value="Business">Business</SelectItem>
              <SelectItem value="Premium">Premium</SelectItem>
            </SelectContent>
          </Select>
        </Row>
      </section>
      <section>
        <SectionTitle label="Advance" required />
        <Row label="Catch Password"><input type="checkbox" data-testid="set-catch-pw" checked={form.catch_password} onChange={(e) => setForm({ ...form, catch_password: e.target.checked })} /></Row>
      </section>
      <div className="flex justify-center pt-2">
        <Button type="submit" disabled={saving} data-testid="set-save" className="rounded-sm h-10 px-8"><FloppyDisk size={14} className="mr-2" /> {saving ? "Saving…" : "Update"}</Button>
      </div>
    </form>
  );
}

/* ---------------- DOCUMENTS (KYC) ---------------- */
function DocumentsTab({ sub, reload }) {
  const inputRefs = useRef({});
  const [previews, setPreviews] = useState({}); // { doc_key: dataURL }
  const [subtypes, setSubtypes] = useState(sub.kyc_subtypes || {});
  const upload = async (docType, file) => {
    if (!file) return;
    try {
      const data_base64 = await fileToBase64(file);
      await http.post(`/subscribers/${sub.id}/kyc`, { doc_type: docType, filename: file.name, content_type: file.type || "application/octet-stream", data_base64 });
      if (subtypes[docType]) {
        await http.patch(`/subscribers/${sub.id}`, { kyc_subtypes: { ...subtypes } });
      }
      toast.success(`${docType} uploaded`);
      reload();
    } catch { toast.error("Upload failed"); }
  };
  const fetchPreview = async (docType) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/subscribers/${sub.id}/kyc/${docType}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      const b = await r.blob();
      const dataUrl = URL.createObjectURL(b);
      setPreviews((p) => ({ ...p, [docType]: { url: dataUrl, mime: b.type } }));
    } catch { /* silent */ }
  };
  useEffect(() => {
    (KYC_TYPES).forEach((t) => { if ((sub.kyc || {})[t.key]) fetchPreview(t.key); });
    // eslint-disable-next-line
  }, [sub.id]);
  const download = (docType, filename) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/subscribers/${sub.id}/kyc/${docType}`;
    fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then((r) => r.blob()).then((b) => {
        const objUrl = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = objUrl; a.download = filename || `${docType}.bin`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
      }).catch(() => toast.error("Download failed"));
  };
  return (
    <div data-testid="documents-tab">
      <div className="text-xs mb-3">
        <span className="text-destructive">*</span> Indicates mandatory document for kyc verification.
      </div>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))] text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left py-3 px-4 border-b border-r border-border">Name</th>
              <th className="text-left py-3 px-4 border-b border-r border-border">Upload</th>
              <th className="text-left py-3 px-4 border-b border-r border-border">Preview</th>
              <th className="text-left py-3 px-4 border-b border-border">Action</th>
            </tr>
          </thead>
          <tbody>
            {KYC_TYPES.map((t) => {
              const doc = (sub.kyc || {})[t.key];
              const preview = previews[t.key];
              return (
                <tr key={t.key} className="border-b border-border last:border-b-0 align-top">
                  <td className="py-4 px-4 border-r border-border w-[220px]">
                    {t.required && <span className="text-destructive">*</span>}<span className="font-medium">{t.label}</span>
                  </td>
                  <td className="py-4 px-4 border-r border-border w-[360px] space-y-2">
                    {t.subtypes.length > 0 && (
                      <div className="flex items-center gap-3">
                        <span className="text-xs w-14 text-muted-foreground">Type</span>
                        <Select value={subtypes[t.key] || t.subtypes[0]} onValueChange={(v) => setSubtypes((s) => ({ ...s, [t.key]: v }))}>
                          <SelectTrigger className="h-8 text-xs w-64" data-testid={`doc-subtype-${t.key}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{t.subtypes.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <span className="text-xs w-14 text-muted-foreground">Upload</span>
                      <input ref={(el) => (inputRefs.current[t.key] = el)} type="file" accept="image/*,.pdf"
                             className="text-xs" data-testid={`doc-file-${t.key}`}
                             onChange={(e) => upload(t.key, e.target.files?.[0])} />
                    </div>
                    {doc?.filename && <div className="text-[10px] font-mono text-muted-foreground truncate max-w-[300px]">{doc.filename}</div>}
                  </td>
                  <td className="py-4 px-4 border-r border-border w-[220px]">
                    {doc ? (
                      preview?.mime?.startsWith("image/") ? (
                        <img src={preview.url} alt={t.label} className="max-h-24 border border-border" data-testid={`doc-preview-${t.key}`} />
                      ) : (
                        <div className="text-xs font-mono text-primary" data-testid={`doc-preview-${t.key}`}>{doc.filename}</div>
                      )
                    ) : (
                      <div className="text-xs text-muted-foreground">No Document</div>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" className="h-8 text-xs rounded-sm" onClick={() => inputRefs.current[t.key]?.click()} data-testid={`doc-upload-${t.key}`}>
                        <Upload size={12} className="mr-1" /> Upload
                      </Button>
                      {doc && (
                        <Button size="sm" className="h-8 text-xs rounded-sm bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))/0.85] text-white" onClick={() => download(t.key, doc.filename)} data-testid={`doc-download-${t.key}`}>
                          <DownloadSimple size={12} className="mr-1" /> Download
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- STATISTICS ---------------- */
const fmtBytes = (n) => {
  if (!n || n < 1024) return `${n || 0} Bytes`;
  const kb = n / 1024; if (kb < 1024) return `${kb.toFixed(2)} KB`;
  const mb = kb / 1024; if (mb < 1024) return `${mb.toFixed(2)} MB`;
  const gb = mb / 1024; return `${gb.toFixed(2)} GB`;
};
const fmtUptime = (sec) => {
  if (!sec) return "0 s";
  const d = Math.floor(sec / 86400); const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60); const s = Math.floor(sec % 60);
  return `${d ? d + "D " : ""}${h}H ${m}m ${s}s`;
};

function StatisticsTab({ sub }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  // Rolling live-bandwidth samples: {ts, dl_mbps, ul_mbps}. Kept client-side
  // by diffing the current online_sessions[0] input_octets/output_octets
  // against the previous poll — gives a real-time Mbps line without adding
  // a new backend endpoint.
  const [live, setLive] = useState([]);
  const load = async () => {
    setLoading(true);
    try {
      // Poll the summary AND the direct-poll live-rate endpoint in parallel.
      // `/live-rate` hits the NAS's MikroTik API and gives INSTANT Tx/Rx
      // (bps) — bypasses RADIUS accounting so the graph is <=3 s behind
      // the CPE regardless of Acct-Interim-Interval.
      const [{ data: st }, live_rate_res] = await Promise.all([
        http.get(`/subscribers/${sub.id}/stats-summary`),
        http.get(`/subscribers/${sub.id}/live-rate`).catch(() => ({ data: null })),
      ]);
      setStats(st);
      const rate = live_rate_res?.data;
      setLive((prev) => {
        const now = new Date();
        // Preferred path: NAS-direct poll gave us a live rate → plot it as-is.
        if (rate && rate.active && (rate.tx_bps != null || rate.rx_bps != null)) {
          const dl = (Number(rate.rx_bps) || 0) / 1e6;   // Rx on the CPE = download
          const ul = (Number(rate.tx_bps) || 0) / 1e6;   // Tx on the CPE = upload
          const next = [...prev, {
            ts: now.toISOString(),
            label: now.toLocaleTimeString().slice(0, 8),
            dl_mbps: +dl.toFixed(3),
            ul_mbps: +ul.toFixed(3),
            source: "nas-poll",
          }];
          return next.slice(-100);
        }
        // Fallback: derive from accounting counters (unchanged legacy path).
        const online = (st.online_sessions || [])[0];
        const cur_in = Number(online?.in_bytes || 0);
        const cur_out = Number(online?.out_bytes || 0);
        const last = prev[prev.length - 1];
        if (!last) {
          return [{ ts: now.toISOString(), label: now.toLocaleTimeString().slice(0, 8), dl_mbps: 0, ul_mbps: 0, raw_in: cur_in, raw_out: cur_out }];
        }
        if (last.raw_in === cur_in && last.raw_out === cur_out) {
          const next = [...prev, { ts: now.toISOString(), label: now.toLocaleTimeString().slice(0, 8), dl_mbps: last.dl_mbps, ul_mbps: last.ul_mbps, raw_in: cur_in, raw_out: cur_out, held: true }];
          return next.slice(-100);
        }
        let baselineIdx = prev.length - 1;
        while (
          baselineIdx > 0 &&
          prev[baselineIdx - 1].raw_in === last.raw_in &&
          prev[baselineIdx - 1].raw_out === last.raw_out
        ) {
          baselineIdx--;
        }
        const baseline = prev[baselineIdx];
        const secs = Math.max(1, (now - new Date(baseline.ts)) / 1000);
        const delta_in = cur_in - baseline.raw_in;
        const delta_out = cur_out - baseline.raw_out;
        const dl = delta_in >= 0 ? (delta_in * 8) / 1e6 / secs : last.dl_mbps || 0;
        const ul = delta_out >= 0 ? (delta_out * 8) / 1e6 / secs : last.ul_mbps || 0;
        const next = [...prev, { ts: now.toISOString(), label: now.toLocaleTimeString().slice(0, 8), dl_mbps: +dl.toFixed(3), ul_mbps: +ul.toFixed(3), raw_in: cur_in, raw_out: cur_out }];
        return next.slice(-100);
      });
    } catch { toast.error("Stats load failed"); }
    setLoading(false);
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
    /* eslint-disable-line */
  }, [sub.id]);
  const info = stats?.info_till_now || {};
  const us = stats?.usage_stats || {};
  const online = stats?.online_sessions || [];
  const daily = stats?.daily_30 || [];
  const auth = stats?.auth_log || [];
  return (
    <div className="space-y-6" data-testid="statistics-tab">
      {/* Online Session */}
      <StatsPanel title="Online Session">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground">
            <tr className="border-b border-border">
              {["UserName","Download","Upload","Total","Uptime","StartTime","FramedIPv4","FramedIPv6","DelegatedIPv6","MAC","NAS","Proto.","Action"].map((h) => <th key={h} className="text-left py-2 px-3 uppercase tracking-wide">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {online.length === 0 && <tr><td colSpan="13" className="text-center py-6 text-muted-foreground">No data available in table</td></tr>}
            {online.map((o, i) => (
              <tr key={i} className="border-b border-border last:border-b-0 font-mono">
                <td className="py-2 px-3">{o.username}</td>
                <td className="py-2 px-3">{fmtBytes(o.in_bytes)}</td>
                <td className="py-2 px-3">{fmtBytes(o.out_bytes)}</td>
                <td className="py-2 px-3">{fmtBytes((o.in_bytes || 0) + (o.out_bytes || 0))}</td>
                <td className="py-2 px-3">{fmtUptime(o.session_time)}</td>
                <td className="py-2 px-3">{(o.started_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="py-2 px-3">{o.framed_ip || "—"}</td>
                <td className="py-2 px-3">—</td>
                <td className="py-2 px-3">—</td>
                <td className="py-2 px-3">{o.mac || "—"}</td>
                <td className="py-2 px-3">{o.nas_name || "—"}</td>
                <td className="py-2 px-3">PPPoE</td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`stat-traffic-${i}`}
                      onClick={() => { const el = document.querySelector("[data-testid=daily-usage]"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                      className="inline-flex items-center gap-1 bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))/0.85] text-white text-[10px] px-2 py-1 rounded-sm"
                      title="Scroll to daily/live usage"
                    >Traffic</button>
                    <KickDialog subscriberId={sub.id} username={o.username || sub.username} onDone={load}>
                      <button
                        data-testid={`stat-disconnect-${i}`}
                        className="inline-flex items-center gap-1 bg-destructive hover:bg-destructive/85 text-white text-[10px] px-2 py-1 rounded-sm"
                        title="Kick (RADIUS Disconnect) with optional re-auth block"
                      >Disconnect</button>
                    </KickDialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </StatsPanel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StatsPanel title="Info Till Now">
          <ul className="text-sm divide-y divide-border">
            <StatRow k="Sessions" v={info.sessions || 0} />
            <StatRow k="UpTime" v={fmtUptime(info.uptime_sec)} />
            <StatRow k="Download" v={fmtBytes(info.in_bytes)} />
            <StatRow k="Upload" v={fmtBytes(info.out_bytes)} />
            <StatRow k="Total" v={fmtBytes((info.in_bytes || 0) + (info.out_bytes || 0))} />
          </ul>
        </StatsPanel>

        <StatsPanel title="Usage Statistics">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr className="border-b border-border">
                {["Time/Usage","Sessions","Uptime","Download","Upload","Total"].map((h) => <th key={h} className="text-left py-2 px-3 uppercase tracking-wide">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {[["Last Day", us.day], ["Last Week", us.week], ["Last Month", us.month], ["Last Year", us.year]].map(([label, r]) => (
                <tr key={label} className="border-b border-border last:border-b-0 font-mono">
                  <td className="py-2 px-3 text-primary">{label}</td>
                  <td className="py-2 px-3">{r?.sessions || 0}</td>
                  <td className="py-2 px-3">{fmtUptime(r?.uptime_sec || 0)}</td>
                  <td className="py-2 px-3">{fmtBytes(r?.in_bytes || 0)}</td>
                  <td className="py-2 px-3">{fmtBytes(r?.out_bytes || 0)}</td>
                  <td className="py-2 px-3">{fmtBytes((r?.in_bytes || 0) + (r?.out_bytes || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </StatsPanel>
      </div>

      {/* Daily 30 */}
        <StatsPanel title="Daily Usage (Last 30 Days)" data-testid="daily-usage">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground">
            <tr className="border-b border-border">
              {["Date","Sessions","Uptime","Download","Upload","Total"].map((h) => <th key={h} className="text-left py-2 px-3 uppercase tracking-wide">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {daily.length === 0 && <tr><td colSpan="6" className="text-center py-6 text-muted-foreground">No data available in table</td></tr>}
            {daily.map((r) => (
              <tr key={r.date} className="border-b border-border last:border-b-0 font-mono">
                <td className="py-2 px-3">{r.date}</td>
                <td className="py-2 px-3">{r.sessions}</td>
                <td className="py-2 px-3">{fmtUptime(r.uptime_sec)}</td>
                <td className="py-2 px-3">{fmtBytes(r.in_bytes)}</td>
                <td className="py-2 px-3">{fmtBytes(r.out_bytes)}</td>
                <td className="py-2 px-3">{fmtBytes((r.in_bytes || 0) + (r.out_bytes || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </StatsPanel>

      {/* Top Auth Log — paginated 10/page, backend caps at 100 rows */}
      <TopAuthLog rows={auth} />

      {/* Live traffic — polls every 3s, DL + UL Mbps line chart (Dashboard-style).
           Sits at the TOP of Statistics so operators see the current pipe usage
           the moment they open the tab. */}
      <StatsPanel title="Live Traffic" data-testid="live-traffic"
        right={(() => {
          const last = live[live.length - 1] || { dl_mbps: 0, ul_mbps: 0 };
          const fmt = (v) => (v >= 1 ? v.toFixed(2) : v.toFixed(3));
          return (
            <div className="flex items-center gap-3 text-xs font-mono">
              <span className="text-[hsl(var(--primary))]">↓ {fmt(last.dl_mbps || 0)} Mbps</span>
              <span className="text-[hsl(var(--success))]">↑ {fmt(last.ul_mbps || 0)} Mbps</span>
            </div>
          );
        })()}
      >
        <div className="h-64">
          {live.length < 2 && <div className="h-full grid place-items-center text-xs text-muted-foreground">Warming up… need 2 samples to compute delta.</div>}
          {live.length >= 2 && (
            <ResponsiveContainer>
              <AreaChart data={live} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="livedl" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.55} /><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient>
                  <linearGradient id="liveul" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--success))" stopOpacity={0.45} /><stop offset="100%" stopColor="hsl(var(--success))" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={10} interval="preserveStartEnd" />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} unit=" Mb" />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => `${v} Mbps`} />
                <Area type="monotone" dataKey="dl_mbps" name="Download" stroke="hsl(var(--primary))" fill="url(#livedl)" strokeWidth={1.8} isAnimationActive={false} />
                <Area type="monotone" dataKey="ul_mbps" name="Upload"   stroke="hsl(var(--success))" fill="url(#liveul)" strokeWidth={1.8} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </StatsPanel>
    </div>
  );
}

function StatsPanel({ title, children, right, ...rest }) {
  return (
    <div className="border border-border bg-card overflow-hidden" {...rest}>
      <div className="bg-primary/85 text-primary-foreground px-4 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2">
        <span className="w-1.5 h-1.5 bg-primary-foreground rounded-full" /> <span className="flex-1">{title}</span>
        {right}
      </div>
      <div className="p-4 overflow-x-auto">{children}</div>
    </div>
  );
}

function TopAuthLog({ rows }) {
  const [page, setPage] = useState(1);
  const perPage = 10;
  const totalPages = Math.max(1, Math.ceil((rows?.length || 0) / perPage));
  const p = Math.min(page, totalPages);
  const slice = (rows || []).slice((p - 1) * perPage, p * perPage);
  return (
    <StatsPanel title={`Top Auth Log (${rows?.length || 0} shown · max 100)`} data-testid="top-auth-log"
      right={
        <div className="flex items-center gap-1 text-xs">
          <button onClick={() => setPage((x) => Math.max(1, x - 1))} disabled={p <= 1} data-testid="auth-prev"
                  className="px-2 py-0.5 border border-primary-foreground/40 rounded-sm disabled:opacity-40">Prev</button>
          <span className="font-mono px-1">{p}/{totalPages}</span>
          <button onClick={() => setPage((x) => Math.min(totalPages, x + 1))} disabled={p >= totalPages} data-testid="auth-next"
                  className="px-2 py-0.5 border border-primary-foreground/40 rounded-sm disabled:opacity-40">Next</button>
        </div>
      }>
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr className="border-b border-border">
            {["Username","Password","Status","Message","IP","MAC","NAS","Date/Time"].map((h) => <th key={h} className="text-left py-2 px-3 uppercase tracking-wide">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {slice.length === 0 && <tr><td colSpan="8" className="text-center py-6 text-muted-foreground">No data available in table</td></tr>}
          {slice.map((l, i) => (
            <tr key={i} className="border-b border-border last:border-b-0 font-mono">
              <td className="py-2 px-3">{l.username}</td>
              <td className="py-2 px-3">••••</td>
              <td className="py-2 px-3">{l.event || l.status || "—"}</td>
              <td className="py-2 px-3 max-w-[300px] truncate">{l.message || "—"}</td>
              <td className="py-2 px-3">{l.ip || l.framed_ip || "—"}</td>
              <td className="py-2 px-3">{l.mac || "—"}</td>
              <td className="py-2 px-3">{l.nas_name || l.nas_ip || "—"}</td>
              <td className="py-2 px-3">{(l.ts || "").slice(0, 19).replace("T", " ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </StatsPanel>
  );
}
function StatRow({ k, v }) {
  return (
    <li className="flex items-center justify-between py-2">
      <span className="text-muted-foreground uppercase-label">{k}</span>
      <span className="font-mono">{v}</span>
    </li>
  );
}

/* ---------------- INVENTORIES ---------------- */
function InventoriesTab({ data, olts, onSave, saving }) {
  const s = data.subscriber;
  const [form, setForm] = useState({
    olt_id: s.olt_id || "", olt_port: s.olt_port ?? "", onu_serial: s.onu_serial || "",
    mac_address: s.mac_address || "",
  });
  useEffect(() => {
    setForm({ olt_id: s.olt_id || "", olt_port: s.olt_port ?? "", onu_serial: s.onu_serial || "", mac_address: s.mac_address || "" });
  }, [s.id, s.olt_id, s.olt_port, s.onu_serial, s.mac_address]);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, olt_port: form.olt_port === "" ? null : Number(form.olt_port) }); }} className="grid grid-cols-2 gap-4 border border-border bg-card p-6" data-testid="inventories-tab">
      <F label="OLT"><Select value={form.olt_id} onValueChange={(v) => setForm({ ...form, olt_id: v })}><SelectTrigger data-testid="inv-olt"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{olts.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} — {o.vendor}</SelectItem>)}</SelectContent></Select></F>
      <F label="OLT Port"><Input type="number" min="1" data-testid="inv-port" value={form.olt_port} onChange={(e) => setForm({ ...form, olt_port: e.target.value })} /></F>
      <F label="ONU Serial (Sr No)"><Input data-testid="inv-serial" value={form.onu_serial} onChange={(e) => setForm({ ...form, onu_serial: e.target.value })} className="font-mono" placeholder="HWTC12345678" /></F>
      <F label="ONU / CPE MAC"><Input data-testid="inv-mac" value={form.mac_address} onChange={(e) => setForm({ ...form, mac_address: e.target.value.toUpperCase() })} className="font-mono" placeholder="AA:BB:CC:DD:EE:FF" /></F>
      <div className="col-span-2"><Button type="submit" disabled={saving} data-testid="inv-save" className="rounded-sm"><FloppyDisk size={14} className="mr-2" /> Save inventory</Button></div>
    </form>
  );
}

const F = ({ label, span = 1, children }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <Label className="uppercase-label">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

// Read-only field — renders as plain "*Label   value" text (no <input>) so
// non-editable rows (ID, Username, Portal login) visually contrast with the
// editable inputs beside them. Matches the SNS reference: label on top, value
// as plain body text underneath, monospace when the value is an identifier.
const RO = ({ label, value, testId }) => (
  <div>
    <Label className="uppercase-label">{label}</Label>
    <div className="mt-1.5 h-9 flex items-center px-3 text-sm text-foreground/80 font-mono border border-transparent" data-testid={testId}>
      {value}
    </div>
  </div>
);
